import React, { useEffect, useMemo, useState } from 'react';
import {
  ACTIVE_TRANSFERS,
  HANDLED_TRANSFERS,
  synthHandledTransfer,
} from '../../warm-transfer/mockData';
import type { WarmTransferDemoState } from '../../warm-transfer/types';
import { formatLongestWait } from '../../warm-transfer/utils';
import { WarmTransferDetailEmpty } from './WarmTransferDetailEmpty';
import { WarmTransferDetailPanel } from './WarmTransferDetailPanel';
import { WarmTransferPageHeader } from './WarmTransferPageHeader';
import { WarmTransferRecentPanel } from './WarmTransferRecentPanel';
import './warm-transfer.css';

export interface WarmTransferViewProps {
  demoState?: WarmTransferDemoState;
}

export function WarmTransferView({ demoState = 'active' }: WarmTransferViewProps) {
  const [selectedId, setSelectedId] = useState<string | null>('t1');
  const [search, setSearch] = useState('');

  const transfers = demoState === 'active' ? ACTIVE_TRANSFERS : [];
  const handled = HANDLED_TRANSFERS;

  const headerStats = useMemo(() => {
    const longestSec = transfers.reduce((m, tr) => Math.max(m, tr.duration || 0), 0);
    return {
      waiting: transfers.length,
      pickedUp: handled.length,
      longest: transfers.length ? formatLongestWait(longestSec) : '0m',
      attention: transfers.filter(
        (tr) => tr.priority === 'priority' || tr.status === 'frustrated',
      ).length,
    };
  }, [transfers, handled]);

  useEffect(() => {
    if (demoState === 'active') {
      setSelectedId(transfers[0]?.id ?? handled[0]?.id ?? null);
    } else {
      setSelectedId(handled[0]?.id ?? null);
    }
  }, [demoState]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredActive = useMemo(() => {
    if (!search) return transfers;
    const q = search.toLowerCase();
    return transfers.filter(
      (tr) =>
        tr.patient.name.toLowerCase().includes(q) || tr.reason.toLowerCase().includes(q),
    );
  }, [transfers, search]);

  const filteredHandled = useMemo(() => {
    if (!search) return handled;
    const q = search.toLowerCase();
    return handled.filter((it) => it.patient.toLowerCase().includes(q));
  }, [handled, search]);

  const selectedActive = transfers.find((tr) => tr.id === selectedId);
  const isHandled = !selectedActive && handled.some((h) => h.id === selectedId);
  const selectedHandled = isHandled ? handled.find((h) => h.id === selectedId) : null;
  const selectedTransfer = selectedActive
    ? selectedActive
    : selectedHandled
      ? synthHandledTransfer(selectedHandled)
      : null;
  const allQuiet = transfers.length === 0;

  return (
    <div className="wt-root mx-auto flex w-full max-w-[1320px] flex-col px-8 pb-10 pt-7">
      <WarmTransferPageHeader search={search} onSearchChange={setSearch} stats={headerStats} />

      <div className="flex items-start gap-[18px]">
        <WarmTransferRecentPanel
          active={filteredActive}
          handled={filteredHandled}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {!selectedTransfer ? (
          <WarmTransferDetailEmpty allQuiet={allQuiet} />
        ) : (
          <WarmTransferDetailPanel transfer={selectedTransfer} isHandled={isHandled} />
        )}
      </div>

      <footer className="mt-7 flex items-center gap-2.5 border-t border-[var(--cream-2)] px-1 py-4 text-[12px] text-[var(--ink-2)]">
        <span className="elva-live h-[6px] w-[6px]" />
        <span className="font-medium">Live</span>
        <span className="wt-mono">· Elva keeps the patient on the line while you pick up</span>
        <span className="wt-mono ml-auto text-[11px] opacity-70">
          elva v2 · apple era · warm transfer
        </span>
      </footer>
    </div>
  );
}
